import React from "react";
import { useEffect } from "react";
import PropTypes from "prop-types";
import { MODAL_TEXT } from "../../ui/modalStyles";
import ModalCloseX from "../common/ModalCloseX";

const SIZE_CLASSES = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-3xl",
  xl: "max-w-4xl",
  xxl: "max-w-[1100px]",
  wide: "max-w-[1180px]",
  xxxl: "max-w-[1400px]",
};

export default function SBINModal({
  isOpen,
  onClose,
  title,
  subtitle = null,
  children,
  footer = null,
  size = "md",
  fitContent = false,
  showClose = true,
  closeOnBackdrop = false,
  closeOnEscape = false,
  panelClassName = "",
  overlayClassName = "",
  headerClassName = "",
  bodyClassName = "",
  footerClassName = "",
}) {
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeOnEscape, onClose]);

  if (!isOpen) return null;
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const heightClass = fitContent ? "h-auto max-h-[90vh]" : "h-[90vh] max-h-[90vh]";

  return (
    <div
      className={`fixed inset-0 z-[10050] flex items-center justify-center bg-black/60 px-4 py-6 ${overlayClassName}`}
      onMouseDown={(event) => {
        if (closeOnBackdrop && event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        className={`relative w-full ${sizeClass} ${heightClass} min-h-0 rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden flex flex-col ${MODAL_TEXT} ${panelClassName}`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className={`flex items-center justify-between bg-rose-500 px-6 py-4 ${headerClassName}`}>
          <h2 className="text-xl md:text-2xl font-semibold text-white">
            {title}
          </h2>
          {showClose ? <ModalCloseX onClick={onClose} /> : null}
        </div>
        {subtitle ? (
          <div className="px-6 py-5 text-lg md:text-xl font-semibold text-slate-900 flex-shrink-0">
            {subtitle}
          </div>
        ) : null}
        <div
          className={`flex-1 min-h-0 overflow-y-auto px-6 py-6 ${bodyClassName}`}
          style={{ scrollbarGutter: "stable" }}
        >
          {children}
        </div>
        {footer ? (
          <div className={`border-t border-slate-200 px-6 py-4 flex-shrink-0 ${footerClassName}`}>
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

SBINModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  children: PropTypes.node.isRequired,
  footer: PropTypes.node,
  size: PropTypes.oneOf(["sm", "md", "lg", "xl", "xxl", "wide", "xxxl"]),
  fitContent: PropTypes.bool,
  showClose: PropTypes.bool,
  closeOnBackdrop: PropTypes.bool,
  closeOnEscape: PropTypes.bool,
  panelClassName: PropTypes.string,
  overlayClassName: PropTypes.string,
  headerClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
  footerClassName: PropTypes.string,
};
